import { useState, useEffect, useRef, useCallback } from 'react';
import { Timer, SkipForward, Minus, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { computeRemainingSeconds, formatTimer } from '@/lib/restTimer';

interface RestTimerProps {
  /** Aikaleima (ms) jolloin lepo päättyy, tai null kun ajastin ei käy. */
  targetEnd: number | null;
  /** Tämän levon kokonaiskesto sekunteina (progress-palkkia varten). */
  durationSeconds: number;
  onComplete: () => void;
  onSkip: () => void;
  /** Muuta jäljellä olevaa aikaa (esim. −15 / +15 sekuntia). */
  onAdjust: (deltaSeconds: number) => void;
}

export function RestTimer({
  targetEnd,
  durationSeconds,
  onComplete,
  onSkip,
  onAdjust,
}: RestTimerProps) {
  const [now, setNow] = useState(() => Date.now());
  const audioCtxRef = useRef<AudioContext | null>(null);
  // Estä useampi laukaisu samalle levolle (aikaleima = tunniste).
  const firedRef = useRef<number | null>(null);

  // Päivitä nykyhetki 500 ms välein kun ajastin käy. Laskenta tehdään
  // Date.now():sta → oikea aika myös taustalta palatessa.
  useEffect(() => {
    if (targetEnd == null) return;
    setNow(Date.now());
    const interval = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(interval);
  }, [targetEnd]);

  const remaining = targetEnd == null ? 0 : computeRemainingSeconds(targetEnd, now);

  const playBeep = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new AudioContext();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') ctx.resume();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 800;
      gain.gain.value = 0.3;
      osc.start();
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
      osc.stop(ctx.currentTime + 0.5);
    } catch {
      /* ääni ei käytettävissä — ei kriittistä */
    }
  }, []);

  // Luo/vapauta AudioContext ensimmäisestä käyttäjän eleestä (selainrajoitus).
  const ensureAudioContext = useCallback(() => {
    try {
      if (!audioCtxRef.current) audioCtxRef.current = new AudioContext();
    } catch {
      /* ohita */
    }
  }, []);

  // Laukaise loppusignaali kerran kun aika täyttyy.
  useEffect(() => {
    if (targetEnd == null) {
      firedRef.current = null;
      return;
    }
    if (remaining <= 0 && firedRef.current !== targetEnd) {
      firedRef.current = targetEnd;
      if (navigator.vibrate) {
        try {
          navigator.vibrate([200, 100, 200]);
        } catch {
          /* ohita */
        }
      }
      playBeep();
      onComplete();
    }
  }, [remaining, targetEnd, onComplete, playBeep]);

  if (targetEnd == null) return null;

  const progress = durationSeconds > 0 ? Math.min(1, remaining / durationSeconds) : 0;
  const colorClass =
    progress > 0.5 ? 'text-green-500' : progress > 0.2 ? 'text-yellow-500' : 'text-red-500';
  const barClass =
    progress > 0.5 ? 'bg-green-500' : progress > 0.2 ? 'bg-yellow-500' : 'bg-red-500';

  return (
    <div
      className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-4 space-y-3"
      role="timer"
      aria-live="off"
      aria-label={`Lepo, ${formatTimer(remaining)} jäljellä`}
    >
      {/* Aikanäyttö + ohita */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Timer className={cn('h-5 w-5', colorClass)} />
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Lepo</span>
        </div>
        <span className={cn('text-3xl font-bold tabular-nums', colorClass)}>
          {formatTimer(remaining)}
        </span>
        <button
          type="button"
          onClick={() => {
            ensureAudioContext();
            onSkip();
          }}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium',
            'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
            'transition-colors touch-manipulation hover:bg-gray-300 dark:hover:bg-gray-600',
            'active:scale-95'
          )}
          aria-label="Ohita lepo"
        >
          <SkipForward className="h-4 w-4" />
          Ohita
        </button>
      </div>

      {/* Progress-palkki */}
      <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-all duration-500 ease-linear', barClass)}
          style={{ width: `${progress * 100}%` }}
        />
      </div>

      {/* −15 s / +15 s */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => {
            ensureAudioContext();
            onAdjust(-15);
          }}
          className={cn(
            'flex-1 flex items-center justify-center gap-1 py-2 rounded-lg text-sm font-medium min-h-[44px]',
            'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
            'transition-colors touch-manipulation hover:bg-gray-300 dark:hover:bg-gray-600',
            'active:scale-[0.98]'
          )}
          aria-label="Vähennä 15 sekuntia"
        >
          <Minus className="h-4 w-4" />
          15 s
        </button>
        <button
          type="button"
          onClick={() => {
            ensureAudioContext();
            onAdjust(15);
          }}
          className={cn(
            'flex-1 flex items-center justify-center gap-1 py-2 rounded-lg text-sm font-medium min-h-[44px]',
            'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
            'transition-colors touch-manipulation hover:bg-gray-300 dark:hover:bg-gray-600',
            'active:scale-[0.98]'
          )}
          aria-label="Lisää 15 sekuntia"
        >
          <Plus className="h-4 w-4" />
          15 s
        </button>
      </div>
    </div>
  );
}
